import {
  createEntityAdapter,
  createReducer,
  EntityState,
} from '@reduxjs/toolkit'
import { WritableDraft } from 'immer'

import { Message } from '@/model/message'
import htmlToText from '@/utils/html-to-text'

import {
  messageNamespace,
  readAllMessages,
  upsertMessage,
} from './message-actions'

export type MessageState = Record<number, Record<number, EntityState<Message, string> | undefined> | undefined>
const messageAdapter = createEntityAdapter<Message>({
  sortComparer: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
})
export const messageSelectors = messageAdapter.getSelectors()

const handleMessageStateChange = (
  draft: WritableDraft<MessageState>,
  creatorId: number,
  fanUserId: number,
  handler: (state: WritableDraft<EntityState<Message, string>>) => WritableDraft<EntityState<Message, string>>,
) => {
  const fanUserMap = draft[creatorId] || {}
  if (!draft[creatorId]) {
    draft[creatorId] = fanUserMap
  }
  const messageState = fanUserMap[fanUserId]
  if (messageState) {
    handler(messageState)
  } else {
    fanUserMap[fanUserId] = handler(messageAdapter.getInitialState())
  }
}

const initialState: MessageState = {}

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(readAllMessages.fulfilled, (draft, { payload: { messages }, meta }) => {
    const { arg: { creatorId, fanUserId } } = meta
    handleMessageStateChange(
      draft,
      creatorId,
      fanUserId,
      (state) => {
        return messageAdapter.upsertMany(state, messages)
      },
    )
  })

  builder.addCase(upsertMessage, (draft, { payload }) => {
    const { creatorId, message } = payload
    handleMessageStateChange(draft, creatorId, Number(message.channel.channelID), (state) => {
      const selectors = messageAdapter.getSelectors()
      const sendingMessage = selectors
        .selectAll(state)
        .find((item) => {
          return item.localStatus !== undefined && htmlToText(item.content.text) === htmlToText(message.content.text)
        })
      if (sendingMessage) {
        messageAdapter.removeOne(state, sendingMessage.id)
      }

      if (selectors.selectIds(state).includes(message.id)) {
        return messageAdapter.updateOne(state, { id: message.id, changes: message })
      }
      return messageAdapter.addOne(state, message)
    })
  })
})

export default { [messageNamespace]: reducer }
